import { QueryClient, QueryFunction } from "@tanstack/react-query";

const PUBLIC_PATHS = ["/auth/", "/terms", "/privacy"];

export function handle401Redirect(url: string) {
  const authPaths = ["/api/auth/login", "/api/auth/me", "/api/auth/logout"];
  if (authPaths.some(p => url.includes(p))) return;
  if (PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p))) return;
  // GAP-025: First-time visitors (no prior session, nashid_sh not set) are sent to
  // the login page with a ?redirect= param so they land on their intended URL after
  // authenticating. Returning users with an expired session go to /auth/expired.
  const hasSessionHint = typeof localStorage !== "undefined" && localStorage.getItem("nashid_sh") === "1";
  if (hasSessionHint) {
    window.location.href = "/auth/expired";
  } else {
    const redirect = window.location.pathname + window.location.search;
    window.location.href = `/auth/login?redirect=${encodeURIComponent(redirect)}`;
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(`${status}: ${message}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    if (res.status === 401) {
      handle401Redirect(res.url);
    }
    const text = (await res.text()) || res.statusText;
    let body: unknown = null;
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
    if (res.status === 402) {
      window.dispatchEvent(new CustomEvent("upgrade-required", { detail: body }));
    }
    throw new ApiError(res.status, text, body);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { headers?: Record<string, string> },
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (data !== undefined) headers["Content-Type"] = "application/json";
  if (options?.headers) {
    for (const [k, v] of Object.entries(options.headers)) {
      headers[k] = v;
    }
  }
  const res = await fetch(url, {
    method,
    headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  const origJson = res.json.bind(res);
  (res as any).json = async () => {
    const body = await origJson();
    return unwrapSAEnvelope(body);
  };
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";

function isSAEnvelope(body: unknown): body is { data: unknown; _scope: { type: string } } {
  if (body === null || typeof body !== "object") return false;
  const rec = body as Record<string, unknown>;
  return (
    "data" in rec &&
    "_scope" in rec &&
    rec._scope !== null &&
    typeof rec._scope === "object" &&
    "type" in (rec._scope as Record<string, unknown>)
  );
}

export function unwrapSAEnvelope<T = unknown>(body: unknown): T {
  if (isSAEnvelope(body)) {
    return body.data as T;
  }
  return body as T;
}

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;

    // Guard: a missing or malformed URL would silently become a network error
    // that is very hard to diagnose. Fail fast with a structured error and emit
    // a Sentry capture so the call site can be fixed. (Task #2054 — TRI-068)
    if (!url || typeof url !== "string" || (!url.startsWith("/") && !url.startsWith("http"))) {
      const err = new Error(`[queryClient] Invalid query URL: ${JSON.stringify(url)}`);
      try {
        // Sentry may not be loaded in all environments — catch silently.
        const Sentry = await import("@sentry/react");
        Sentry.captureMessage("Invalid query URL", {
          level: "error",
          extra: { url, queryKey: queryKey.map(String) },
        });
      } catch { /* Sentry unavailable */ }
      throw err;
    }

    const res = await fetch(url, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null as never;
    }

    if (res.status === 401) {
      handle401Redirect(res.url);
    }

    await throwIfResNotOk(res);
    const body = await res.json();
    return unwrapSAEnvelope(body);
  };

/**
 * Unwraps a paginated API response { data: T[] } or plain T[] into a flat array.
 * Use as the `select` option in useQuery when the component only needs the items.
 */
export function selectListData<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[];
  if (res !== null && typeof res === "object" && "data" in res && Array.isArray((res as Record<string, unknown>).data)) {
    return (res as { data: T[] }).data;
  }
  return [];
}

export async function fetchList<T>(url: string): Promise<T[]> {
  const res = await fetch(url, { credentials: "include" });
  if (res.status === 401) {
    handle401Redirect(url);
    return [];
  }
  if (!res.ok) return [];
  const body: unknown = await res.json().catch(() => null);
  if (Array.isArray(body)) return body as T[];
  if (body !== null && typeof body === "object" && "data" in body && Array.isArray((body as Record<string, unknown>).data)) {
    return (body as { data: T[] }).data;
  }
  return [];
}

/**
 * Refresh intervals for real-time, operational, and administrative data.
 *
 * GAP-032 — Safety-critical screens must poll at REALTIME (15 s); background
 * data at OPERATIONAL (60 s). ADMINISTRATIVE disables automatic polling so
 * only explicit invalidations trigger refetches.
 *
 * Safety-critical query keys — each must also set refetchOnWindowFocus: true
 * so the data re-fetches immediately when the user switches back to the tab:
 *   - ["/api/ptw-v2/permits", "operations-live"]  (active permit board)
 *   - ["/api/ptw/simops-reviews"]                  (SIMOPS holds board)
 *   - ["/api/muster/sessions"]                     (muster session status)
 *   - ["/api/muster/responses", sessionId]         (muster responses)
 *   - ["/api/muster-events"]                       (legacy muster events)
 */
export const REFRESH_INTERVALS = {
  REALTIME: 15_000,
  OPERATIONAL: 60_000,
  ADMINISTRATIVE: false as false,
} as const;

/**
 * Cache policy tiers for differentiated staleTime / gcTime across query types.
 *
 * GAP-036 — Apply at individual useQuery call sites via spread:
 *   useQuery({ ...CACHE_POLICY.OPERATIONAL, queryKey: [...] })
 *
 * Tiers:
 *   REALTIME      — live safety-critical data; always fresh, keep for 60 s
 *   OPERATIONAL   — dashboards / live lists; 30 s grace, keep for 5 min
 *   ADMINISTRATIVE — config / permissions / settings; 5 min grace, keep 15 min
 *   STATIC        — catalogs / reference data; 30 min grace, keep for 60 min
 */
export const CACHE_POLICY = {
  REALTIME: {
    staleTime: 0,
    gcTime: 60_000,
  },
  OPERATIONAL: {
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  },
  ADMINISTRATIVE: {
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
  },
  STATIC: {
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  },
} as const;

/**
 * Retry predicate for TanStack Query.
 *
 * Retries up to 5 times (with exponential backoff starting at 1 s) only when
 * the server explicitly signals it is still initialising (503 SERVER_STARTING).
 * All other errors — including generic 503s, auth errors, and application
 * errors — are NOT retried so the UI surfaces them immediately.
 */
function serverStartingRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status === 503) {
    const body = error.body as Record<string, unknown> | null;
    const code = (body as any)?.error?.code ?? (body as any)?.code;
    if (code === "SERVER_STARTING") {
      return failureCount < 5;
    }
  }
  return false;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      retry: serverStartingRetry,
      retryDelay: (failureCount) => Math.min(1_000 * Math.pow(2, failureCount), 30_000),
    },
    mutations: {
      retry: false,
    },
  },
});
